'use client'
import React, { useState, useRef, useEffect } from 'react'
import { FaFacebook, FaLine, FaWhatsapp, FaTelegramPlane, FaRedditAlien } from 'react-icons/fa'
import { FaXTwitter } from 'react-icons/fa6'
import { MdEmail, MdContentCopy, MdCheck, MdShare } from 'react-icons/md'
import ShareButton from '@components/ShareButton'

const PLATFORM_META = {
  facebook: { label: 'Facebook', Icon: FaFacebook, color: '#1877f2', build: (url) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}` },
  line:     { label: 'LINE',     Icon: FaLine,     color: '#00b900', build: (url) => `https://social-plugins.line.me/lineit/share?url=${encodeURIComponent(url)}` },
  twitter:  { label: 'X',        Icon: FaXTwitter, color: '#000000', build: (url, title) => `https://x.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}` },
  whatsapp: { label: 'WhatsApp', Icon: FaWhatsapp, color: '#25d366', build: (url, title) => `https://wa.me/?text=${encodeURIComponent(`${title} ${url}`)}` },
  telegram: { label: 'Telegram', Icon: FaTelegramPlane, color: '#26a5e4', build: (url, title) => `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}` },
  reddit:   { label: 'Reddit',   Icon: FaRedditAlien,   color: '#ff4500', build: (url, title) => `https://www.reddit.com/submit?url=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}` },
  email:    { label: 'Email',    Icon: MdEmail,    color: '#6b7280', build: (url, title) => `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(url)}` },
}

/**
 * Renders the post's share affordance according to the admin-configured mode:
 *  - 'single'     → the existing native ShareButton (device share sheet) — this
 *                   already carries its own "Share" text/icon, so no extra label
 *                   is added around it.
 *  - 'platforms'  → a row of individual platform icon links. These have no text
 *                   of their own (only per-icon aria-labels, which are invisible),
 *                   so a visible label defaults ON here unless explicitly disabled.
 *  - 'expandable' → one button that opens a small popover of platform icons.
 *
 * `config` comes from website.blogPage.sharing:
 *   { mode, platforms: string[], buttonBg, buttonColor, showLabel, label, labelPosition }
 * `label` may be a per-language object ({ en, th, ... }) or a legacy plain string.
 * `showLabel` follows the same "default on unless explicitly false" convention
 * used elsewhere in this editor (m.showDate, m.showShare, etc.) so existing saved
 * configs that predate this field still show the label rather than silently
 * hiding it.
 */
export default function BlogShareBar({ config = {}, title, path, cover, dictionary, language = 'en' }) {
  const mode = config.mode ?? 'single'
  const platforms = (config.platforms?.length ? config.platforms : ['facebook', 'line', 'twitter', 'whatsapp', 'copy'])
  const [open, setOpen] = useState(false)
  const [copied, setCopied] = useState(false)
  const popRef = useRef(null)

  useEffect(() => {
    if (!open) return
    const onClick = (e) => { if (popRef.current && !popRef.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open])

  const origin = process.env.NEXT_PUBLIC_SHARE_URL || (typeof window !== 'undefined' ? window.location.origin : '')
  const fullUrl = `${origin}/${path}`

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 1600)
    } catch {
      // clipboard may be unavailable — fail silently
    }
  }

  const iconRow = (
    <div className="flex flex-row flex-wrap items-center gap-2">
      {platforms.map((p) => {
        if (p === 'copy') {
          return (
            <button
              key="copy"
              type="button"
              onClick={handleCopy}
              aria-label={copied ? 'Link copied' : 'Copy link'}
              title={copied ? 'Copied!' : 'Copy link'}
              className="flex items-center justify-center w-8 h-8 rounded-full transition-opacity hover:opacity-80"
              style={{ background: '#374151', color: '#fff' }}
            >
              {copied ? <MdCheck size={14} /> : <MdContentCopy size={13} />}
            </button>
          )
        }
        const meta = PLATFORM_META[p]
        if (!meta) return null
        const { Icon } = meta
        return (
          <a
            key={p}
            href={meta.build(fullUrl, title)}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Share on ${meta.label}`}
            title={meta.label}
            className="flex items-center justify-center w-8 h-8 rounded-full transition-opacity hover:opacity-80"
            style={{ background: meta.color, color: '#fff' }}
          >
            <Icon size={13} />
          </a>
        )
      })}
    </div>
  )

  let widget
  if (mode === 'single') {
    widget = <ShareButton dictionary={dictionary} title={title} text={title} url={path} cover={cover} />
  } else if (mode === 'platforms') {
    widget = iconRow
  } else {
    // expandable
    widget = (
      <div className="relative inline-flex items-center" ref={popRef}>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          aria-label="Share this post"
          className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border transition-colors"
          style={{
            background: config.buttonBg || '#f8fafc',
            color: config.buttonColor || '#374151',
            borderColor: '#e2e8f0',
          }}
        >
          <MdShare size={14} /> Share
        </button>
        {open && (
          <div className="absolute right-0 top-full mt-2 z-20 bg-white border border-gray-200 rounded-lg shadow-lg p-2.5">
            {iconRow}
          </div>
        )}
      </div>
    )
  }

  // 'single' mode's ShareButton already carries its own "Share" text/icon —
  // don't stack a second label on top of it.
  if (mode === 'single' || config.showLabel === false) return widget

  const labelText = (typeof config.label === 'object' ? (config.label?.[language] || config.label?.en) : config.label) || 'Share:'
  if (!labelText) return widget

  const labelEl = <span className="text-sm text-gray-500 whitespace-nowrap">{labelText}</span>
  const position = config.labelPosition ?? 'left'

  if (position === 'top') {
    return (
      <div className="flex flex-col items-start gap-1.5">
        {labelEl}
        {widget}
      </div>
    )
  }
  return (
    <div className="flex flex-row items-center gap-2">
      {position === 'left' && labelEl}
      {widget}
      {position === 'right' && labelEl}
    </div>
  )
}